import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { createApiClient, isServiceUnavailable, type GuestSessionResponse, type MeResponse } from "@keydials/api-client";
import { resetSoleDialLanding } from "../lib/landing";
import { endedReasonOf, hasEnded, onTrialGate, trialGateHit, type AccessEndedReason } from "./trialGate";

// Distinct from the admin app's kde.* keys so the two apps don't clobber each other's
// tokens on a shared localhost origin.
const ACCESS_KEY = "kdu.accessToken";
const REFRESH_KEY = "kdu.refreshToken";

// Kiosk auto-lock (KEY-268). The lock ticket lives under its own key, alongside the dial it is bound to and
// that dial's centre image, because the locked screen has to render before it is allowed to ask the server
// anything: the ticket is refused /api/me by design, so the client tracks its own locked state.
const LOCK_TICKET_KEY = "kdu.lockTicket";
const LOCK_INSTANCE_KEY = "kdu.lockInstanceId";
const LOCK_IMAGE_KEY = "kdu.lockImageUrl";

// Guest access (KEY-297). The session itself lives under the ordinary token keys — a guest session IS the
// session — but the grant's expiry is client-persisted so the banner can say when access ends without a
// round trip (/api/me deliberately stays a claims read).
const GUEST_EXPIRY_KEY = "kdu.guestAccessExpiresAt";

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "http://localhost:5218";

// KEY-604: the access token is short-lived (15 minutes in production) and this app never used the refresh token,
// so a session quietly died mid-use — every call 401'd, the page kept showing what it had, and nothing sent the
// person to sign in. A 401 now renews the session from the refresh token and retries the call; only when that
// fails is the session ended, which drops the user on the login screen instead of a page of dead requests.
let refreshInFlight: Promise<string | null> | null = null;
let onSessionEnded: (() => void) | null = null;

function renewSession(rejectedToken: string): Promise<string | null> {
  // A locked kiosk holds a ticket, not a session: its 401s are the lock screen's business, and clearing here
  // would throw the ticket away.
  if (readLock()) {
    return Promise.resolve(null);
  }
  // Someone else (another tab, an earlier call) already rotated the token this request was sent with.
  const current = localStorage.getItem(ACCESS_KEY);
  if (current && current !== rejectedToken) {
    return Promise.resolve(current);
  }
  // Single-flight: a page fires a burst of requests, and each rotating the refresh token would revoke the last.
  if (refreshInFlight) {
    return refreshInFlight;
  }
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  const run = (async () => {
    try {
      if (!refreshToken) {
        throw new Error("no refresh token");
      }
      const tokens = await refreshClient.refresh(refreshToken);
      localStorage.setItem(ACCESS_KEY, tokens.accessToken);
      localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
      return tokens.accessToken;
    } catch (e) {
      // KEY-732: a refused refresh token ends the session; an unreachable API does not. A 503 or a dropped connection while a deploy swaps the
      // API says nothing about the tokens, so they stay, and the caller sees that failure rather than a sign-out.
      if (refreshToken && isServiceUnavailable(e)) {
        throw e;
      }
      localStorage.removeItem(ACCESS_KEY);
      localStorage.removeItem(REFRESH_KEY);
      localStorage.removeItem(GUEST_EXPIRY_KEY);
      onSessionEnded?.();
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  refreshInFlight = run;
  return run;
}

// The refresh call itself is unauthenticated, so it needs no 401 handler — and must not have one.
const refreshClient = createApiClient(apiBaseUrl, { clientApp: "web-user" });

// A 402 from any request is the access gate — an ended trial (KEY-637) or, since KEY-911, a cancelled
// subscription; see ./trialGate for who listens and why.
export const api = createApiClient(apiBaseUrl, {
  clientApp: "web-user",
  onUnauthorized: renewSession,
  onPaymentRequired: trialGateHit,
});

export const getAccessToken = (): string | null => localStorage.getItem(ACCESS_KEY);

/** A device sitting behind the kiosk lock screen: which dial it is bound to, and what to show on it. */
export interface LockState {
  ticket: string;
  instanceId: string;
  imageUrl: string | null;
}

function readLock(): LockState | null {
  const ticket = localStorage.getItem(LOCK_TICKET_KEY);
  const instanceId = localStorage.getItem(LOCK_INSTANCE_KEY);
  return ticket && instanceId ? { ticket, instanceId, imageUrl: localStorage.getItem(LOCK_IMAGE_KEY) } : null;
}

function clearLock() {
  localStorage.removeItem(LOCK_TICKET_KEY);
  localStorage.removeItem(LOCK_INSTANCE_KEY);
  localStorage.removeItem(LOCK_IMAGE_KEY);
}

interface AuthState {
  me: MeResponse | null;
  loading: boolean;
  /** KEY-732: the session check at start-up couldn't reach the API (mid-deploy, offline) and is retrying with the
   * tokens kept. `loading` stays true meanwhile; this only changes what the loading screen says. */
  reconnecting: boolean;
  error: string | null;
  requestCode: (email: string) => Promise<void>;
  verifyCode: (email: string, code: string) => Promise<boolean>;
  logout: () => Promise<void>;
  /** Re-read /api/me. Needed after editing your own account: the header greeting, the avatar and the SMS
   * channel gate all read off this, and none of them would change until the next sign-in otherwise. */
  refreshMe: () => Promise<void>;
  /**
   * The server has said this account's access is over (KEY-431, KEY-637, KEY-911): a card-free trial ran out, or
   * the subscription was cancelled. On /api/me at sign-in, or as a 402 to any request since — a dial that was open
   * when it happened, as much as My Dials. App.tsx renders the access-ended screen off this, above every route,
   * so no page has to catch the 402 itself.
   */
  accessEnded: boolean;
  /** Why, when `accessEnded`: /api/me's `endedReason`, else the 402's `code`, else "trial" (the older wording). */
  endedReason: AccessEndedReason | null;

  // --- Kiosk auto-lock (KEY-268) ---
  /** Non-null while the device is locked. The app renders nothing but the lock screen in this state. */
  lock: LockState | null;
  /** Drop this device to the lock screen for the dial it has open. Best-effort: a failure leaves the
   * session exactly as it was rather than stranding the user on a screen they can't unlock. */
  lockTo: (instanceId: string, imageUrl?: string | null) => Promise<boolean>;
  /** Spend a PIN on the lock ticket. Resolves false for any rejection — the server never says which. */
  unlock: (userId: string, pin: string) => Promise<boolean>;
  /** True while this session came from a PIN and may only reach one dial. */
  pinnedToInstanceId: string | null;

  // --- Guest access (KEY-297) ---
  /** Set while this is a guest session: the one dial the invite covers. */
  guestInstanceId: string | null;
  /** When the guest's access ends (ISO), for the banner. Null outside a guest session. */
  guestAccessExpiresAt: string | null;
  /** Adopt a freshly verified guest session, replacing whatever session was here before. */
  enterGuestSession: (session: GuestSessionResponse) => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [reconnecting, setReconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lock, setLock] = useState<LockState | null>(() => readLock());
  // KEY-637: a 402 has arrived under this session. Provisional until /api/me confirms it — see the effects below.
  const [gated, setGated] = useState(false);
  // KEY-911: the reason the 402 gave, if it gave one — words the screen until /api/me says it for itself.
  const [gatedReason, setGatedReason] = useState<AccessEndedReason | null>(null);

  // KEY-604: a session that could not be renewed lands here, and `!me` is what shows the login screen.
  useEffect(() => {
    onSessionEnded = () => {
      resetSoleDialLanding();
      setMe(null);
    };
    return () => {
      onSessionEnded = null;
    };
  }, []);

  useEffect(() => {
    // A locked device deliberately never calls /api/me: the ticket carries no capabilities and the server
    // refuses it that route, so asking would look like a broken session and sign the kiosk out.
    if (readLock()) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    const load = () => {
      // Re-read each time: a renewal on an earlier attempt may have rotated it.
      const token = localStorage.getItem(ACCESS_KEY);
      if (!token) {
        setLoading(false);
        return;
      }
      api
        .me(token)
        .then((loaded) => {
          if (cancelled) return;
          setMe(loaded);
          setReconnecting(false);
          setLoading(false);
        })
        .catch((e) => {
          if (cancelled) return;
          // KEY-732: the API mid-deploy or the network down keeps the tokens and tries again, so a deploy no
          // longer signs out everyone with the app open. Any other failure is the server's answer: the session is over.
          if (isServiceUnavailable(e)) {
            setReconnecting(true);
            retry = setTimeout(load, Math.min(2000 * 2 ** attempt++, 15000));
            return;
          }
          localStorage.removeItem(ACCESS_KEY);
          localStorage.removeItem(REFRESH_KEY);
          setReconnecting(false);
          setLoading(false);
        });
    };
    load();
    return () => {
      cancelled = true;
      clearTimeout(retry);
    };
  }, []);

  const refreshMe = useCallback(async () => {
    const token = localStorage.getItem(ACCESS_KEY);
    if (!token) {
      return;
    }
    try {
      setMe(await api.me(token));
    } catch {
      // Leave the session exactly as it was: a failed refresh is not a reason to sign someone out mid-edit.
    }
  }, []);

  // KEY-637: a 402 from anywhere means the trial ended under an open session. The flag switches the screen at
  // once — it is set in the same tick as the page's own catch, so no refusal is ever painted — and the /api/me
  // re-read that follows is what confirms it, or withdraws it (next effect) should the server say otherwise.
  useEffect(
    () =>
      onTrialGate((reason) => {
        setGated(true);
        setGatedReason(reason);
        void refreshMe();
      }),
    [refreshMe],
  );

  // The flag is provisional until /api/me has spoken: a fresh `me` that has not ended withdraws it, and so does
  // signing out — the next person to sign in on this browser must not inherit a stranger's ended trial.
  useEffect(() => {
    setGated((current) => current && hasEnded(me));
    if (!hasEnded(me)) setGatedReason(null);
  }, [me]);

  const lockTo = useCallback(async (instanceId: string, imageUrl?: string | null) => {
    const token = localStorage.getItem(ACCESS_KEY);
    if (!token) return false;

    try {
      // The refresh token goes with the request so the server can revoke it: the whole point is that the
      // session behind the lock screen can no longer be resumed, only re-authenticated.
      const ticket = await api.lock(token, instanceId, localStorage.getItem(REFRESH_KEY));
      localStorage.setItem(LOCK_TICKET_KEY, ticket.accessToken);
      localStorage.setItem(LOCK_INSTANCE_KEY, ticket.instanceId);
      if (imageUrl) {
        localStorage.setItem(LOCK_IMAGE_KEY, imageUrl);
      }
      localStorage.removeItem(ACCESS_KEY);
      localStorage.removeItem(REFRESH_KEY);
      setMe(null);
      setLock({ ticket: ticket.accessToken, instanceId: ticket.instanceId, imageUrl: imageUrl ?? null });
      return true;
    } catch {
      return false;
    }
  }, []);

  const unlock = useCallback(async (userId: string, pin: string) => {
    const current = readLock();
    if (!current) return false;

    try {
      const session = await api.pinVerify(current.ticket, userId, pin);
      localStorage.setItem(ACCESS_KEY, session.accessToken);
      // No refresh token by design: a PIN session lives exactly as long as its access token, after which
      // the device falls back to the lock screen (or a full sign-in) rather than rolling on unattended.
      localStorage.removeItem(REFRESH_KEY);
      clearLock();
      setLock(null);
      setMe(await api.me(session.accessToken));
      return true;
    } catch {
      return false;
    }
  }, []);

  const value = useMemo<AuthState>(() => {
    async function requestCode(email: string) {
      setError(null);
      await api.requestCode({ email });
    }

    async function verifyCode(email: string, code: string) {
      setError(null);
      try {
        const tokens = await api.verifyCode({ email, code });
        localStorage.setItem(ACCESS_KEY, tokens.accessToken);
        localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
        setMe(await api.me(tokens.accessToken));
        return true;
      } catch {
        setError("Invalid or expired code.");
        return false;
      }
    }

    async function logout() {
      const refreshToken = localStorage.getItem(REFRESH_KEY);
      if (refreshToken) {
        try {
          await api.logout(refreshToken);
        } catch {
          // best-effort
        }
      }
      localStorage.removeItem(ACCESS_KEY);
      localStorage.removeItem(REFRESH_KEY);
      localStorage.removeItem(GUEST_EXPIRY_KEY);
      // Signing out of a locked kiosk is the documented way back to the full login screen.
      clearLock();
      setLock(null);
      // The next sign-in gets its own "straight to my only dial" hop.
      resetSoleDialLanding();
      setMe(null);
    }

    async function enterGuestSession(session: GuestSessionResponse) {
      // A guest link may be opened over any existing session (including a locked kiosk) — the invite's
      // session simply replaces it, exactly as a fresh sign-in would.
      clearLock();
      setLock(null);
      localStorage.setItem(ACCESS_KEY, session.accessToken);
      localStorage.setItem(REFRESH_KEY, session.refreshToken);
      localStorage.setItem(GUEST_EXPIRY_KEY, session.accessExpiresAtUtc);
      setMe(await api.me(session.accessToken));
    }

    return {
      me,
      loading,
      reconnecting,
      error,
      requestCode,
      verifyCode,
      logout,
      refreshMe,
      accessEnded: !!me && (hasEnded(me) || gated),
      endedReason: endedReasonOf(me, gatedReason),
      lock,
      lockTo,
      unlock,
      pinnedToInstanceId: me?.pinnedToInstanceId ?? null,
      guestInstanceId: me?.guestInstanceId ?? null,
      guestAccessExpiresAt: me?.guestInstanceId ? localStorage.getItem(GUEST_EXPIRY_KEY) : null,
      enterGuestSession,
    };
  }, [me, loading, reconnecting, error, gated, gatedReason, lock, lockTo, unlock, refreshMe]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
